import React, { useEffect, useRef, useState } from "react";
import { api, fmtEUR, fmtTRY, useAuth } from "../lib/api";
import { Plus, Pencil, Trash2, X, Check, Users as UsersIcon, UserPlus, UserMinus, ListChecks, Camera, Loader2, Anchor } from "lucide-react";
import PassengersModal from "../components/PassengersModal";

const emptyPerson = { full_name: "", tc: "", dob: "", gender: "E", avatar_url: "" };
const emptyDraft = { name: "", total_eur: 2240, offset_try: 0, occupants: "", people: [{ ...emptyPerson }] };

export default function Cabins() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cabins, setCabins] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [showPax, setShowPax] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/cabins");
      setCabins(Array.isArray(data) ? data : []);
    } catch (e) { console.error("Cabins load:", e); }
  };
  useEffect(() => {
    load();
  }, []);

  if (!user) return null;

  const startEdit = (c) => {
    setEditingId(c.id);
    setDraft({
      name: c.name || "",
      total_eur: c.total_eur ?? 2240,
      offset_try: c.offset_try ?? 0,
      occupants: (c.occupants || []).join(", "),
      people: (c.people && c.people.length > 0) ? c.people.map((p) => ({ ...emptyPerson, ...p })) : [{ ...emptyPerson }],
    });
    setShowNew(false);
  };

  const startNew = () => {
    setEditingId(null);
    setShowNew(true);
    setDraft({ ...emptyDraft, people: [{ ...emptyPerson }] });
  };

  const cancel = () => { setEditingId(null); setShowNew(false); setDraft(emptyDraft); };

  const save = async () => {
    const body = {
      name: draft.name.trim(),
      total_eur: parseFloat(String(draft.total_eur).replace(",", ".")) || 0,
      offset_try: parseFloat(String(draft.offset_try).replace(",", ".")) || 0,
      occupants: draft.occupants.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      people: draft.people.map((p) => ({
        full_name: p.full_name || "",
        tc: p.tc || "",
        dob: p.dob || "",
        gender: p.gender || "",
        avatar_url: p.avatar_url || "",
      })),
    };
    if (!body.name) return;
    if (editingId) await api.put(`/cabins/${editingId}`, body);
    else await api.post(`/cabins`, body);
    cancel();
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kabin ve tüm ödemeleri silinecek. Emin misiniz?")) return;
    await api.delete(`/cabins/${id}`);
    await load();
  };

  const addPerson = () => setDraft((d) => ({ ...d, people: [...d.people, { ...emptyPerson }] }));
  const removePerson = (idx) => setDraft((d) => ({ ...d, people: d.people.filter((_, i) => i !== idx) }));
  const updPerson = (idx, k, v) => setDraft((d) => ({ ...d, people: d.people.map((p, i) => (i === idx ? { ...p, [k]: v } : p)) }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="cabins-passengers-btn" onClick={() => setShowPax(true)} className="tk-btn-ghost flex items-center gap-1.5">
            <ListChecks className="w-4 h-4" /> Yolcu Listesi
          </button>
          {isAdmin && (
            <button data-testid="add-cabin-btn" onClick={startNew} className="tk-btn flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Yeni Kabin
            </button>
          )}
        </div>
      </div>

      {isAdmin && showNew && (
        <div className="tk-card p-4" data-testid="new-cabin-card">
          <CabinForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} addPerson={addPerson} removePerson={removePerson} updPerson={updPerson} />
        </div>
      )}

      <div className="space-y-3">
        {cabins.map((c) => {
          if (isAdmin && editingId === c.id) {
            return (
              <div key={c.id} className="tk-card p-4" data-testid={`edit-cabin-${c.id}`}>
                <CabinForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} addPerson={addPerson} removePerson={removePerson} updPerson={updPerson} />
              </div>
            );
          }
          return (
            <div key={c.id} data-testid={`cabin-admin-${c.id}`} className="tk-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Kabin {c.order}</div>
                  <div className="text-lg font-display font-semibold flex flex-wrap items-baseline gap-x-2">
                    <span>{c.name}</span>
                    {c.occupants?.length > 0 && (
                      <span className="text-sm font-normal" style={{ color: "hsl(var(--text-2))" }}>
                        Kullanıcılar: {c.occupants.map((u) => "@" + u).join(", ")}
                      </span>
                    )}
                  </div>
                  {/* 2 satır alt toplam — Tekne Toplam / Alışveriş Toplam (Ödenen yeşil, Kalan kırmızı bold) */}
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span><b style={{ color: "hsl(var(--text-1))" }}>Tekne Toplam:</b> {fmtEUR(c.total_eur || 0)}</span>
                      <span className="text-[hsl(var(--text-2))]">|</span>
                      <span><span style={{ color: "hsl(var(--success))" }}>Ödenen:</span> <span style={{ color: "hsl(var(--success))" }}>{fmtEUR(c.paid_eur || 0)}</span></span>
                      <span className="text-[hsl(var(--text-2))]">|</span>
                      <span><span style={{ color: "hsl(var(--error))" }}>Kalan:</span> <b style={{ color: "hsl(var(--error))" }}>{fmtEUR(Math.max(0, (c.total_eur || 0) - (c.paid_eur || 0)))}</b></span>
                      <span className="text-[11px] italic" style={{ color: "hsl(var(--text-2))" }}>(Tahmini maliyetler gerçekleştikçe tutarlar güncellenir.)</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span><b style={{ color: "hsl(var(--text-1))" }}>Alışveriş Toplam:</b> {fmtTRY(c.shopping_total_try || 0)}</span>
                      <span className="text-[hsl(var(--text-2))]">|</span>
                      <span><span style={{ color: "hsl(var(--success))" }}>Ödenen:</span> <span style={{ color: "hsl(var(--success))" }}>{fmtTRY(c.shopping_paid_try || 0)}</span></span>
                      <span className="text-[hsl(var(--text-2))]">|</span>
                      <span><span style={{ color: "hsl(var(--error))" }}>Kalan:</span> <b style={{ color: "hsl(var(--error))" }}>{fmtTRY(Math.max(0, (c.shopping_total_try || 0) - (c.shopping_paid_try || 0)))}</b></span>
                      <span className="text-[11px] italic" style={{ color: "hsl(var(--text-2))" }}>(Tahmini maliyetler gerçekleştikçe tutarlar güncellenir.)</span>
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button data-testid={`edit-cabin-btn-${c.id}`} onClick={() => startEdit(c)} className="tk-btn-ghost !py-2 !px-2.5"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-cabin-btn-${c.id}`} onClick={() => remove(c.id)} className="tk-btn-danger !py-2 !px-2.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              {c.people?.length > 0 && (
                <div className="mt-3 pt-3 tk-divider space-y-2">
                  {c.people.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <PersonAvatar
                        person={p}
                        cabinId={c.id}
                        idx={i}
                        canEdit={isAdmin}
                        onChange={load}
                      />
                      <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <b>{p.full_name || "—"}</b>
                        {p.tc && <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>TC: {p.tc}</span>}
                        {p.dob && <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>Doğum: {new Date(p.dob).toLocaleDateString("tr-TR")}</span>}
                        {p.gender && <span className="tk-chip">{p.gender === "K" ? "Kadın" : p.gender === "E" ? "Erkek" : p.gender}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* === Tekne Diğer Maliyetler === (Alışveriş listesi mantığı) */}
      <OtherCostsSection isAdmin={isAdmin} />

      {showPax && <PassengersModal cabins={cabins} onClose={() => setShowPax(false)} />}
    </div>
  );
}

function OtherCostsSection({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ target_try: 0, target_eur: 0, paid_try: 0, paid_eur: 0, rate: 44 });
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", unit_cost: "", currency: "TRY", description: "" });

  const load = async () => {
    const [{ data: it }, { data: s }] = await Promise.all([
      api.get("/other-costs"),
      api.get("/diger-maliyet-summary"),
    ]);
    setItems(it || []);
    setSummary(s || {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = {
      name: draft.name.trim(),
      unit_cost: parseFloat(String(draft.unit_cost).replace(",", ".")) || 0,
      currency: draft.currency === "EUR" ? "EUR" : "TRY",
      description: draft.description || "",
    };
    if (!body.name) return;
    if (editingId) await api.put(`/other-costs/${editingId}`, body);
    else await api.post(`/other-costs`, body);
    setShowNew(false); setEditingId(null); setDraft({ name: "", unit_cost: "", currency: "TRY", description: "" });
    await load();
  };
  const startEdit = (it) => {
    setEditingId(it.id);
    setDraft({ name: it.name, unit_cost: it.unit_cost, currency: it.currency || "TRY", description: it.description || "" });
    setShowNew(false);
  };
  const remove = async (id) => {
    if (!window.confirm("Kalem silinsin mi?")) return;
    await api.delete(`/other-costs/${id}`); await load();
  };

  return (
    <div className="tk-card p-4 space-y-3" data-testid="other-costs-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <Anchor className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
          <h3 className="text-lg font-display font-semibold whitespace-nowrap">Tekne Diğer Maliyetler</h3>
          {isAdmin && (
            <button data-testid="oc-new-btn" onClick={() => { setShowNew(true); setEditingId(null); setDraft({ name: "", unit_cost: "", currency: "TRY", description: "" }); }} className="ml-auto md:hidden tk-btn flex items-center gap-1.5 shrink-0 !py-1.5 !px-2.5 text-xs">
              <Plus className="w-3.5 h-3.5" /> Yeni
            </button>
          )}
        </div>

        {/* Ortada: Toplam ₺ · Toplam € · Ödenen ₺ · Kalan ₺ — düz yazı, kendi renkleri.
            Mobilde 2-col grid; masaüstünde eşit aralıklı tek satır. */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 md:flex md:items-center md:justify-center md:gap-6 md:flex-1 md:flex-wrap text-sm" data-testid="oc-inline-stats">
          <span className="whitespace-nowrap" style={{ color: "hsl(var(--text-2))" }}>Toplam (₺): <b style={{ color: "hsl(var(--text-1))" }}>{fmtTRY(summary.target_try)}</b></span>
          <span className="whitespace-nowrap" style={{ color: "hsl(var(--text-2))" }}>Toplam (€): <b style={{ color: "hsl(var(--text-1))" }}>{fmtEUR(summary.target_eur)}</b></span>
          <span className="whitespace-nowrap" style={{ color: "hsl(var(--success))" }}>Ödenen (₺): <b>{fmtTRY(summary.paid_try)}</b></span>
          <span className="whitespace-nowrap" style={{ color: "hsl(var(--error))" }}>Kalan (₺): <b>{fmtTRY(Math.max(0, summary.target_try - summary.paid_try))}</b></span>
        </div>

        {isAdmin && (
          <button data-testid="oc-new-btn-desktop" onClick={() => { setShowNew(true); setEditingId(null); setDraft({ name: "", unit_cost: "", currency: "TRY", description: "" }); }} className="hidden md:flex tk-btn items-center gap-1.5 shrink-0">
            <Plus className="w-4 h-4" /> Yeni
          </button>
        )}
      </div>

      {(showNew || editingId) && (
        <div className="tk-card p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="tk-label">Kalem Adı</label>
            <input data-testid="oc-name-input" className="tk-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Bahşiş" />
          </div>
          <div>
            <label className="tk-label">Birim Tutar</label>
            <input data-testid="oc-cost-input" className="tk-input" inputMode="decimal" value={draft.unit_cost} onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })} />
          </div>
          <div>
            <label className="tk-label">Para Birimi</label>
            <select data-testid="oc-currency-select" className="tk-input" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
              <option value="TRY">₺ (TRY)</option>
              <option value="EUR">€ (EUR)</option>
            </select>
          </div>
          <div>
            <label className="tk-label">Açıklama</label>
            <input data-testid="oc-desc-input" className="tk-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button data-testid="oc-cancel-btn" onClick={() => { setShowNew(false); setEditingId(null); }} className="tk-btn-ghost flex items-center gap-1.5"><X className="w-4 h-4" /> Vazgeç</button>
            <button data-testid="oc-save-btn" onClick={save} className="tk-btn flex items-center gap-1.5"><Check className="w-4 h-4" /> Kaydet</button>
          </div>
        </div>
      )}

      {/* Kalemler */}
      <div className="space-y-2">
        {items.length === 0 && !showNew && (
          <div className="text-sm text-center py-4" style={{ color: "hsl(var(--text-2))" }}>Henüz kalem eklenmedi.</div>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`oc-row-${it.id}`} className="tk-card p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium">{it.name}</div>
              {it.description && <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>{it.description}</div>}
            </div>
            <div className="text-right">
              <div className="font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>
                {it.currency === "EUR" ? fmtEUR(it.unit_cost) : fmtTRY(it.unit_cost)}
              </div>
              <div className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>{it.currency}</div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <button data-testid={`oc-edit-${it.id}`} onClick={() => startEdit(it)} className="tk-btn-ghost !py-1.5 !px-2"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`oc-delete-${it.id}`} onClick={() => remove(it.id)} className="tk-btn-danger !py-1.5 !px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CabinForm({ draft, setDraft, onSave, onCancel, addPerson, removePerson, updPerson }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="tk-label">Kabin Adı</label>
          <input data-testid="cabin-name-input" className="tk-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="örn. Nur & Aytuğ" />
        </div>
        <div>
          <label className="tk-label">Toplam (€)</label>
          <input data-testid="cabin-total-input" className="tk-input" inputMode="decimal" value={draft.total_eur} onChange={(e) => setDraft({ ...draft, total_eur: e.target.value })} />
        </div>
        <div>
          <label className="tk-label">₺ Düzeltme (offset)</label>
          <input data-testid="cabin-offset-input" className="tk-input" inputMode="decimal" value={draft.offset_try} onChange={(e) => setDraft({ ...draft, offset_try: e.target.value })} placeholder="0" />
        </div>
        <div className="md:col-span-4">
          <label className="tk-label">Kullanıcı adları (virgülle, küçük harf)</label>
          <input data-testid="cabin-occupants-input" className="tk-input" value={draft.occupants} onChange={(e) => setDraft({ ...draft, occupants: e.target.value })} placeholder="örn. nur, aytuğ" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="tk-label !mb-0">Yolcular</label>
          <button type="button" data-testid="cabin-add-person-btn" onClick={addPerson} className="tk-btn-ghost !py-1.5 !px-2.5 text-xs flex items-center gap-1">
            <UserPlus className="w-3.5 h-3.5" /> Kişi Ekle
          </button>
        </div>
        <div className="space-y-3">
          {draft.people.map((p, idx) => (
            <div key={idx} data-testid={`cabin-person-${idx}`} className="rounded-xl p-3 grid grid-cols-1 md:grid-cols-5 gap-2" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
              <div className="md:col-span-2">
                <label className="tk-label">Ad Soyad</label>
                <input data-testid={`cabin-person-name-${idx}`} className="tk-input" value={p.full_name} onChange={(e) => updPerson(idx, "full_name", e.target.value)} />
              </div>
              <div>
                <label className="tk-label">T.C. Kimlik No</label>
                <input data-testid={`cabin-person-tc-${idx}`} className="tk-input" inputMode="numeric" maxLength={11} value={p.tc} onChange={(e) => updPerson(idx, "tc", e.target.value.replace(/\D/g, "").slice(0, 11))} />
              </div>
              <div>
                <label className="tk-label">Doğum Tarihi</label>
                <input data-testid={`cabin-person-dob-${idx}`} type="date" className="tk-input" value={p.dob} onChange={(e) => updPerson(idx, "dob", e.target.value)} />
              </div>
              <div>
                <label className="tk-label">Cinsiyet</label>
                <div className="flex gap-1">
                  <select data-testid={`cabin-person-gender-${idx}`} className="tk-input" value={p.gender} onChange={(e) => updPerson(idx, "gender", e.target.value)}>
                    <option value="E">Erkek</option>
                    <option value="K">Kadın</option>
                  </select>
                  {draft.people.length > 1 && (
                    <button type="button" data-testid={`cabin-person-remove-${idx}`} onClick={() => removePerson(idx)} className="tk-btn-danger !py-2 !px-2">
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button data-testid="cabin-cancel-btn" onClick={onCancel} className="tk-btn-ghost flex items-center gap-1.5"><X className="w-4 h-4" /> Vazgeç</button>
        <button data-testid="cabin-save-btn" onClick={onSave} className="tk-btn flex items-center gap-1.5"><Check className="w-4 h-4" /> Kaydet</button>
      </div>
    </div>
  );
}


function avatarColor(seed) {
  const palette = ["#ff6b35", "#4992ff", "#16a34a", "#a855f7", "#ec4899", "#0ea5e9", "#f59e0b", "#14b8a6"];
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function PersonAvatar({ person, cabinId, idx, canEdit, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const av = person?.avatar_url || "";
  const src = av ? (av.startsWith("http") ? av : `${backend}${av}`) : null;
  const initials = (person?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const onPick = () => { if (canEdit && !busy) fileRef.current?.click(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/cabins/${cabinId}/people/${idx}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onChange?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Yüklenemedi");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const removeAv = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Profil fotosu silinsin mi?")) return;
    setBusy(true);
    try {
      await api.delete(`/cabins/${cabinId}/people/${idx}/avatar`);
      await onChange?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 44, height: 44 }}
      data-testid={`person-avatar-${cabinId}-${idx}`}
    >
      <button
        type="button"
        onClick={onPick}
        disabled={!canEdit || busy}
        className={`w-full h-full rounded-full overflow-hidden flex items-center justify-center font-semibold text-white transition ${canEdit ? "hover:ring-2 hover:ring-[#4992ff] cursor-pointer" : "cursor-default"}`}
        style={{ backgroundColor: avatarColor(person?.full_name || cabinId), fontSize: 16 }}
        title={canEdit ? "Profil fotosu yükle" : ""}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : src ? (
          <img src={src} alt={person?.full_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span>{initials}</span>
        )}
      </button>
      {canEdit && !busy && (
        <span
          className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "#4992ff", border: "2px solid white" }}
        >
          <Camera className="w-2.5 h-2.5" style={{ color: "white" }} />
        </span>
      )}
      {canEdit && av && !busy && (
        <button
          onClick={removeAv}
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "#dc2626", border: "2px solid white" }}
          title="Fotoğrafı sil"
          data-testid={`person-avatar-remove-${cabinId}-${idx}`}
        >
          <X className="w-2.5 h-2.5" style={{ color: "white" }} />
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid={`person-avatar-input-${cabinId}-${idx}`}
      />
    </div>
  );
}
